import { Route } from '@angular/router';
import { dskhomeAuthGuard } from '@zhik/core/guards/dskhome-auth';
import { dskhomeMaintenanceEnvironmentGuardFactory } from '@zhik/core/guards/dskhome-maintenance';
import { dskhomeIsBackofficerGuard } from '@zhik/core/guards/dskhome-user-roles';
import { dskhomeVerifiedIdCardGuard } from '@zhik/core/guards/dskhome-verified-id-card';
import { dskhomeVerifiedPhoneNumberGuard } from '@zhik/core/guards/dskhome-verified-phone-number';
import { EnvironmentTypes } from '@zhik/core/models/environments';
import { sequentialGuards } from '@zhik/core/utils/sequential-guards';

export const appRoutes: Route[] = [
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full'
  },
  {
    path: 'home',
    title: $localize`Начало`,
    loadComponent: () => import('./pages/home-page/home-page.component').then(c => c.HomePageComponent)
  },
  {
    path: 'backoffice',
    canActivate: [sequentialGuards(dskhomeAuthGuard, dskhomeIsBackofficerGuard)],
    children: [
      {
        path: '',
        title: $localize`Административни функции за бекофис потребители`,
        loadComponent: () => import('./pages/backoffice-page/backoffice-page.component').then(c => c.BackofficePageComponent),
      },
      {
        path: 'codebtor',
        title: $localize`Административни функции, свързани със съдлъжници по кредит`,
        loadComponent: () => import('./pages/backoffice-codebtor-page/backoffice-codebtor-page.component').then(c => c.BackofficeCodebtorPageComponent),
      },
    ],
  },
  // @Info: Active this maintenance page when needed
  {
    path: 'maintenance',
    title: $localize`Информация за планирана поддръжка`,
    loadComponent: () => import('./pages/maintenance-page/maintenance-page.component').then(c => c.MaintenancePageComponent)
  },
  {
    path: 'schedule-appointment',
    loadChildren: () => import('@zhik/mortgage-consultation').then(r => r.mortgageConsultationRoutes)
  },
  {
    path: 'listings',
    children: [
      {
        path: '',
        loadChildren: () => import('@zhik/listings').then(r => r.LISTING_ROUTES),
      },
      {
        path: ':id',
        loadChildren: () => import('@zhik/listing-details').then(r => r.LISTING_DETAILS_ROUTES)
      },
    ]
  },
  {
    path: 'mortgage-application',
    children: [
      {
        path: '',
        redirectTo: 'userguide',
        pathMatch: 'full',
      },
      {
        path: 'userguide',
        title: $localize`Кандидатствай за кредит`,
        loadChildren: () => import('@zhik/user-guide').then(c => c.userGuideRoutes)
      },
      {
        path: 'credit-requests',
        canActivate: [
          sequentialGuards(
            dskhomeAuthGuard,
            dskhomeVerifiedIdCardGuard,
            dskhomeVerifiedPhoneNumberGuard
          )
        ],
        loadChildren: () => import('@zhik/mortgage-application').then((m) => m.MORTGAGE_APPLICATION_ROUTES),
      },
    ]
  },
  {
    path: 'preliminary-approval/:processId/codebtor/:codebtorId',
    loadChildren: () => import('@zhik/preliminary-codebtor').then((m) => m.PRELIMINARY_CODEBTOR_ROUTES),
    canActivate: [
      sequentialGuards(
        dskhomeAuthGuard,
        dskhomeVerifiedIdCardGuard,
        dskhomeVerifiedPhoneNumberGuard
      )
    ],
  },
  {
    path: 'final-approval/:processId/codebtor/:codebtorId',
    loadChildren: () => import('@zhik/final-codebtor').then((m) => m.FINAL_CODEBTOR_ROUTES),
    canActivate: [
      sequentialGuards(
        dskhomeAuthGuard,
        dskhomeVerifiedIdCardGuard,
        dskhomeVerifiedPhoneNumberGuard,
        dskhomeMaintenanceEnvironmentGuardFactory({ guardedEnvironments: [EnvironmentTypes.PRODUCTION] })
      )
    ],
  },
  {
    path: 'user-verification',
    title: $localize`Завърши профила си`,
    canActivate: [dskhomeAuthGuard],
    loadChildren: () => import('@zhik/verification-application').then(r => r.VERIFICATION_APPLICATION_ROUTES)
  },
  {
    path: 'market-analysis',
    loadChildren: () => import('@zhik/market-analysis').then(r => r.marketAnalysisRoutes)
  },
  {
    path: 'property-appraisal',
    loadChildren: () => import('@zhik/property-appraisal').then(r => r.propertyAppraisalRoutes)
  },
  {
    path: 'authentication',
    loadChildren: () => import('@zhik/auth').then(r => r.AUTH_ROUTES)
  },
  {
    path: 'terms-and-conditions',
    title: $localize`Политика за поверителност`,
    loadComponent: () =>
      import('./pages/terms-and-conditions-page/terms-and-conditions-page.component').then(c => c.TermsAndConditionsPageComponent)
  },
  {
    path: 'site-rules',
    title: $localize`Правила и условия за ползване`,
    loadComponent: () => import('./pages/site-rules-page/site-rules-page.component').then(c => c.SiteRulesPageComponent)
  },
  {
    path: 'newsletter-subscribe',
    title: $localize`Потвърждаване на абонамент за имотни обяви`,
    loadComponent: () => import('./pages/newsletter-subscribe-verify-page/newsletter-subscribe-verify-page.component').then(c => c.NewsletterSubscribeVerifyPageComponent)
  },
  {
    path: 'newsletter-unsubscribe',
    title: $localize`Прекратяване на абонамент за имотни обяви`,
    loadComponent: () => import('./pages/newsletter-unsubscribe-page/newsletter-unsubscribe-page.component').then(c => c.NewsletterUnsubscribePageComponent)
  },
  {
    path: 'newsletter-unsubscribe/:id',
    title: $localize`Прекратяване на абонамент за имотни обяви`,
    loadComponent: () => import('./pages/newsletter-unsubscribe-page/newsletter-unsubscribe-page.component').then(c => c.NewsletterUnsubscribePageComponent)
  },
  {
    path: 'codebtor-link/:id',
    title: $localize`Съдлъжник за ипотечен кредит`,
    loadComponent: () => import('./pages/codebtor-link-page/codebtor-link-page.component').then(c => c.CodebtorLinkPageComponent)
  },
  {
    path: '**',
    pathMatch: 'full',
    title: $localize`Страницата не може да бъде намерена`,
    loadComponent: () =>
      import('./core/components/page-not-found/page-not-found.component').then(c => c.PageNotFoundComponent)
  }
];
